import { Button, Typography } from "@suraasa/placebo-ui"
import CPATProctoringInstructions from "features/Cpat/CpatOverview/CpatProctoringInstruction"
import { useState } from "react"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"

/**
 * 🧪 Developer Test Page — CPAT Proctoring Flow
 *
 * Route: /testing/proctoring/
 *
 * This page is intended for internal developer testing ONLY.
 * It renders the full CPATProctoringInstructions flow (System Check →
 * Face Capture → Final Dialog) without starting a real assessment.
 *
 * The `onProceed` prop intercepts the final "Proceed to Test" action
 * so no actual CPAT attempt is started.
 */

type FlowState = "running" | "completed"

const CpatProctoringTest = () => {
  const [flowState, setFlowState] = useState<FlowState>("running")
  const [key, setKey] = useState(0) // increment to remount/restart the flow

  const handleRestart = () => {
    setFlowState("running")
    setKey(k => k + 1)
  }

  useHideGlobalBottomNavigation()
  useHideTopNavigation()

  return (
    <div>
      {flowState === "running" ? (
        <CPATProctoringInstructions
          key={key}
          onProceed={() => setFlowState("completed")}
        />
      ) : (
        <CompletionScreen onRestart={handleRestart} />
      )}
    </div>
  )
}

/* ─────────────────────────────────────────
   Completion Screen
───────────────────────────────────────── */
const CompletionScreen = ({ onRestart }: { onRestart: () => void }) => {
  return (
    <div className="flex min-h-[calc(100dvh-34px)] flex-col items-center justify-center gap-6 bg-surface-50 p-8 text-center">
      {/* Icon */}
      <div className="flex size-20 items-center justify-center rounded-full bg-success-100 text-4xl shadow-sm">
        ✅
      </div>

      <div className="max-w-sm space-y-2">
        <Typography variant="title2" className="text-onSurface-900">
          Flow Completed!
        </Typography>
        <Typography variant="body" className="text-onSurface-600">
          The proctoring flow finished successfully. In a real session, the
          assessment would start here.
        </Typography>
      </div>

      {/* Steps summary */}
      <div className="w-full max-w-xs rounded-xl border border-onSurface-200 bg-white p-4 text-left shadow-sm">
        <Typography
          variant="smallBody"
          className="mb-3 font-semibold uppercase tracking-widest text-onSurface-400"
        >
          Steps Completed
        </Typography>
        <div className="space-y-2.5">
          {[
            {
              step: 1,
              label: "System Check",
              desc: "Browser + Camera permissions verified",
            },
            {
              step: 2,
              label: "Face Capture",
              desc: "Photo captured & validated",
            },
            {
              step: 3,
              label: "Final Dialog",
              desc: "Monitoring consent accepted",
            },
          ].map(({ step, label, desc }) => (
            <div key={step} className="flex items-start gap-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-success-500 text-xs font-bold text-white">
                {step}
              </div>
              <div>
                <Typography
                  variant="smallBody"
                  className="font-semibold text-onSurface-900"
                >
                  {label}
                </Typography>
                <Typography variant="smallBody" className="text-onSurface-500">
                  {desc}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="outlined"
        color="black"
        size="md"
        onClick={onRestart}
        className="mt-2"
      >
        ↺ Restart Flow from Step 1
      </Button>

      <Typography
        variant="smallBody"
        className="rounded-lg bg-amber-50 px-4 py-2 text-amber-800"
      >
        🧪 This is a dev-only test page. No real assessment was started.
      </Typography>
    </div>
  )
}

export default CpatProctoringTest
